import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide-core.min.css';
import '@splidejs/splide/dist/css/splide.min.css';

import type { CarouselOptions } from '../types';

export class Carousel
{
    private element: HTMLElement;
    private splide: Splide | null = null;
    private hasDynamicText: boolean;
    private dynamicTextContainer: HTMLElement | null = null;
    private dynamicTexts: HTMLElement[] = [];
    private options: Required<CarouselOptions>;

    constructor(element: HTMLElement)
    {
        this.element = element;

        const slides = this.element.querySelectorAll<HTMLElement>('.splide__slide');
        if (slides.length <= 1)
        {
            return;
        }
        const dataSlidesToShow = element.dataset.slidesToShow;

        this.options = {
            slidesToShow: dataSlidesToShow ? parseInt(dataSlidesToShow, 10) : (this.getSlidesToShow()),
            gap: 20,
            autoplay: false,
            autoplaySpeed: 5000,
            loop: false,
        };

        this.hasDynamicText = element.dataset.dynamicText === 'true';

        if (this.hasDynamicText)
        {
            const parent = element.parentElement;

            if (parent)
            {
                this.dynamicTextContainer = parent.querySelector('.medienprojekt-section__dynamic-text');

                if (this.dynamicTextContainer)
                {
                    this.dynamicTexts = Array.from(this.dynamicTextContainer.querySelectorAll('p[data-slide]'));
                }
            }
        }

        this.init();
    }

    private getSlidesToShow(): number
    {
        const width = window.innerWidth;
        if (width < 600) return 1;
        if (width < 900) return 2;
        if (width < 1200) return 3;
        return 4;
    }

    private init(): void
    {
        const track = this.element.querySelector('.splide__track');

        if (!track)
        {
            return;
        }

        const isSingleSlide = this.options.slidesToShow === 1;

        this.splide = new Splide(this.element, {
            type: isSingleSlide ? 'fade' : (this.options.loop ? 'loop' : 'slide'),
            rewind: false,
            perPage: this.options.slidesToShow,
            gap: isSingleSlide ? 0 : this.options.gap,
            autoplay: this.options.autoplay,
            interval: this.options.autoplaySpeed,
            pauseOnHover: true,
            pauseOnFocus: true,
            arrows: true,
            pagination: isSingleSlide && this.element.dataset.carousel === "medienprojekt" ? true : false,
            drag: true,
            keyboard: true,
            speed: 600,
            easing: 'ease',
            breakpoints: isSingleSlide ? {} :
            {
                1199: { perPage: 3, gap: 20 },
                899: { perPage: 2, gap: 16 },
                599: { perPage: 1, gap: 10 },
            },
        });

        if (this.hasDynamicText && this.dynamicTexts.length > 0)
        {
            this.splide.on('move', (newIndex: number) =>
            {
                this.updateDynamicText(newIndex);
            });
        }

        this.splide.mount();
}

    private updateDynamicText(index: number): void
    {
        if (!this.hasDynamicText || this.dynamicTexts.length === 0) return;

        this.dynamicTexts.forEach((text, i) =>
        {
            if (i === index)
            {
                text.classList.remove('is-hidden');
            }
            else
                {
                text.classList.add('is-hidden');
            }
        });
    }

    public prev(): void
    {
        this.splide?.go('<');
    }

    public next(): void
    {
        this.splide?.go('>');
    }

    public goToSlide(index: number): void
    {
        this.splide?.go(index);
    }

    public destroy(): void
    {
        this.splide?.destroy();
    }
}

export function initCarousels(): void
{
    document.querySelectorAll<HTMLElement>('[data-carousel]').forEach(carousel =>
    {
        new Carousel(carousel);
    });
}
